import { makeAutoObservable } from 'mobx';
import React from 'react';
import { newGuid } from 'tools/guid';
import { translator } from 'core/localization';
import { ColorPalette, PaletteSettings } from 'controls/react/color-picker/types';
import { hexColorPattern, MAX_COLORS_PER_PALETTE } from 'controls/react/color-picker/constants';
import { getSettings } from 'framework/userSettings';
import { ThemedColor } from 'framework/colors/types';
import { ModalPosition, SetModalPositionCallback, openModal } from 'controls/react/ant/antModal';
import { ImportPaletteFormStore } from 'controls/react/color-picker/components/import-palette-form/importPaletteFormStore';
import { ImportPaletteForm } from 'controls/react/color-picker/components/import-palette-form/importPaletteForm';
import { ThemeStore } from 'framework/theme/themeStore';

const i18n = translator({
	'Import palette': { no: 'Importer palett' },
	'Name is required': { no: 'Navn er påkrevd' },
});

export function isValidHexColor(value: string): boolean {
	return hexColorPattern.test(value?.trim() ?? '');
}

const SETTINGS_CATEGORY = 'ColorPalettes';

class ColorPalettesStoreInner {
	settings: PaletteSettings = null;
	loading = false;

	constructor() {
		makeAutoObservable(this);
	}

	init = async () => {
		if (this.settings != null || this.loading) return;

		this.loading = true;

		({ data: this.settings } = await getSettings<PaletteSettings>(SETTINGS_CATEGORY, { clazz: PaletteSettings }));

		if (this.settings.palettes == null) {
			this.settings.palettes = [];
		}

		this.settings.palettes.forEach((palette) => this.migrateColors(palette));

		this.loading = false;
	};

	get palettes(): ColorPalette[] {
		return this.settings?.palettes ?? [];
	}

	private findPalette(id: string) {
		return this.palettes.find((x) => x.id === id);
	}

	addPalette = (name: string, colors: ThemedColor[] = []): ColorPalette => {
		const palette = { id: newGuid(), name, colors: colors.slice(0, MAX_COLORS_PER_PALETTE) };
		this.settings.palettes.push(palette);
		return palette;
	};

	removePalette = (id: string) => {
		const index = this.settings.palettes.findIndex((x) => x.id === id);
		if (index >= 0) {
			this.settings.palettes.splice(index, 1);
		}
	};

	addColorToPalette = (id: string, color: ThemedColor) => {
		const palette = this.findPalette(id);
		if (palette && palette.colors.length < MAX_COLORS_PER_PALETTE) {
			palette.colors.push(color);
		}
	};

	removeColorFromPalette = (id: string, index: number) => {
		this.findPalette(id)?.colors.splice(index, 1);
	};

	openImportPaletteDialog = async (setPosition?: SetModalPositionCallback) => {
		const store = new ImportPaletteFormStore();

		const modal = openModal(
			{
				title: i18n('Import palette'),
				width: 420,
				mask: true,
				footer: null,
				positionType: setPosition ?? ModalPosition.Mouse,
				theme: ThemeStore.theme,
			},
			<ImportPaletteForm store={store} />,
		);

		store.closeModal = modal.close;

		const result = await modal.promise;

		if (result) {
			this.addPalette(
				store.name,
				store.validColors.map((light) => ({ light })),
			);
		}
	};

	moveColor = (palette: ColorPalette, fromIndex: number, toIndex: number) => {
		if (!palette || fromIndex === toIndex) return;

		const [color] = palette.colors.splice(fromIndex, 1);
		if (fromIndex < toIndex) toIndex--;
		palette.colors.splice(toIndex, 0, color);
	};

	movePalette = (fromIndex: number, toIndex: number) => {
		if (fromIndex < 0 || fromIndex === toIndex) return;

		const [palette] = this.settings.palettes.splice(fromIndex, 1);
		if (fromIndex < toIndex) toIndex--;
		this.settings.palettes.splice(toIndex, 0, palette);
	};

	// palettes saved before a color became a pair for the light and the dark theme hold plain hex strings
	private migrateColors(palette: ColorPalette) {
		const colors: (ThemedColor | string)[] = palette.colors;
		if (colors.some((color) => typeof color == 'string')) {
			palette.colors = colors.map((color) => (typeof color == 'string' ? { light: color } : color));
		}
	}
}

export const ColorPalettesStore = new ColorPalettesStoreInner();
